<template>
    <card-list>
        <subheader-list-item icon="fa-align-left" title="Quote" />

        <container-list-item padded no-border>
            <b-field label="Quote Type" label-position="inside">
                <b-select expanded v-model="quote.enrollmentType" @input="onEnrollmentTypeChange">
                    <option value="payment_plan">Payment Plan</option>
                    <option value="cart_enrollment">Reseller Cart Enrollment</option>
                </b-select>
            </b-field>

            <b-field label="Quote Status" label-position="inside">
                <b-select expanded v-model="quote.status">
                    <option value="DRAFT">Draft</option>
                    <option value="REVIEW">Client Review/On Hold</option>
                    <option value="SUBMITTED">Submit for Processing</option>
                </b-select>
            </b-field>

            <b-field label="Notes" label-position="inside">
                <b-input type="textarea" v-model="quote.notes"></b-input>
            </b-field>
        </container-list-item>

        <subheader-list-item icon="fa-user" title="Client Information" />
        <container-list-item padded>
            <b-field>
                <b-autocomplete
                    :data="userQuery"
                    field="title"
                    :loading="isFetching"
                    @typing="searchUsers"
                    @select="selectUser"
                    v-if="!quote.user"
                    icon="search"
                    placeholder="Search...">

                    <template slot-scope="props">
                        <i class="fas fa-user mr-2"></i>{{ props.option.first_name }} {{ props.option.last_name }}<br>
                        <small class="mr-4"><i class="far fa-envelope mr-2"></i>{{ props.option.email_masked }}</small><small><i class="far fa-barcode-alt mr-2"></i>{{ props.option.id }}</small>
                    </template>

                    <!--                    <template slot="footer">-->
                    <!--                        <a @click="$openCard('new-user', {}, card)">-->
                    <!--                            <span>Add new User... </span>-->
                    <!--                        </a>-->
                    <!--                    </template>-->
                    <template slot="empty" v-if="!isFetching">No results.</template>
                </b-autocomplete>
                <div v-else class="columns">
                    <div class="column is-10 pl-5">
                        <i class="fas fa-user mr-2"></i><strong>{{ quote.user.first_name }} {{ quote.user.last_name }}</strong><br>
                        <small class="mr-4"><i class="far fa-envelope mr-2"></i>{{ quote.user.email }}</small><small><i class="far fa-barcode-alt mr-2"></i>{{ quote.user.id }}</small>
                    </div>
                    <div class="column has-text-right">
                        <b-button @click="quote.user=null"><i class="far fa-fw fa-times"></i></b-button>
                    </div>
                </div>
            </b-field>
        </container-list-item>

        <template v-if="quote.user">
            <subheader-list-item icon="fa-address-card" title="Contact Information" />

            <container-list-item no-border>
                <data-row title="Primary Email">{{ quote.user.email || 'N/A' }}</data-row>
                <data-row title="Secondary Email" v-if="quote.user.email2">{{ quote.user.email2 }}</data-row>
                <data-row title="Primary Phone (SMS)">{{ quote.user.phone || 'N/A' }}</data-row>
                <data-row title="Secondary Phone" v-if="quote.user.phone2">{{ quote.user.phone2 }}</data-row>
                <data-row title="Mailing Address">{{ quote.user.address || 'N/A' }}</data-row>
                <data-row title="Address Line 2" v-if="quote.user.address2">{{ quote.user.address2 }}</data-row>
                <data-row title="City">{{ quote.user.city || 'N/A' }}</data-row>
                <data-row title="State">{{ quote.user.state || 'N/A' }}</data-row>
                <data-row title="ZIP Code">{{ quote.user.zip || 'N/A' }}</data-row>
            </container-list-item>

            <container-list-item padded no-border>
                <b-message type="is-info">
                    <strong><i class="fas fa-info-circle mr-2"></i>Please verify contact information with client.</strong><br>
                    If any information is outdated or incorrect, please inform the client to update their profile before submitting this quote. Agreement notifications will be sent to the primary email address on file.
                </b-message>
            </container-list-item>
        </template>

        <subheader-list-item icon="fa-money-check" title="Payment Plan" v-if="quote.enrollmentType === 'payment_plan'" />

        <container-list-item padded v-if="quote.enrollmentType === 'payment_plan' && !hoursValid">
            <b-message type="is-danger">
                Payment plans are only available for 10 clock hours or greater.
            </b-message>
        </container-list-item>

        <container-list-item padded no-border v-if="quote.enrollmentType === 'payment_plan'">
            <div class="columns">
                <div class="column is-3">
                    <b-field label="Clock Hours" label-position="inside">
                        <b-input v-model="quote.hours" required @blur="validateHours"></b-input>
                    </b-field>
                </div>
                <div class="column is-9">
                    <b-field label="Plan Title" label-position="inside">
                        <b-input v-model="quote.title"></b-input>
                    </b-field>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <b-field label="Term (Months)" label-position="inside">
                        <b-input v-model="quote.term" type="number" min="2" max="48"></b-input>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Quoted Price" label-position="inside">
                        <div class="input" style="padding: 2.1rem 0.7rem 1rem;">{{ calculatedTotal | currency }}</div>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Monthly Payment" label-position="inside">
                        <div class="input" style="padding: 2.1rem 0.7rem 1rem;">{{ monthly | currency }}</div>
                    </b-field>
                </div>

                <!--                <div class="column is-4">-->
                <!--                    <b-field label="Down Payment" label-position="inside">-->
                <!--                        <b-select expanded v-model="quote.down">-->
                <!--                            <option value="0">No Down Payment</option>-->
                <!--                            &lt;!&ndash;<option value="10">10% Down</option>&ndash;&gt;-->
                <!--                            &lt;!&ndash;<option value="20">20% Down</option>&ndash;&gt;-->
                <!--                        </b-select>-->
                <!--                    </b-field>-->
                <!--                    -->
                <!--                </div>-->
            </div>
            <div class="columns">

                <div class="column is-5">
                    <b-field label="Deferment Month" label-position="inside">
                        <b-select expanded v-model="quote.defer_month">
                            <option value="01">1 - January</option>
                            <option value="02">2 - February</option>
                            <option value="03">3 - March</option>
                            <option value="04">4 - April</option>
                            <option value="05">5 - May</option>
                            <option value="06">6 - June</option>
                            <option value="07">7 - July</option>
                            <option value="08">8 - August</option>
                            <option value="09">9 - September</option>
                            <option value="10">10 - October</option>
                            <option value="11">11 - November</option>
                            <option value="12">12 - December</option>
                        </b-select>
                    </b-field>
                </div>

                <div class="column is-3">
                    <b-field label="Deferment Day" label-position="inside">
                        <div class="input" style="padding: 2.1rem 0.7rem 1rem;">1</div>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Deferment Year" label-position="inside">
                        <b-select expanded v-model="quote.defer_year">
                            <option v-for="year in deferYears" :key="year" :value="year">{{ year }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

        </container-list-item>

        <subheader-list-item icon="fa-shopping-cart" title="Cart Enrollment" v-if="quote.enrollmentType === 'cart_enrollment'" />

        <container-list-item no-border v-if="quote.enrollmentType === 'cart_enrollment'">
            <data-row title="Courses">{{ quote.courses ? quote.courses.length : 0 }}</data-row>
            <data-row title="Clock Hours">{{ totalCourseHours }}</data-row>
            <data-row title="Cart Total">{{ calculatedTotal | currency }}</data-row>
        </container-list-item>

        <subheader-list-item icon="fa-calculator" title="Sales Tax Information" v-if="quote.user" />

        <container-list-item padded v-if="quote.user && fetchingTax">
            <b-message type="is-info">
                <i class="fas fa-spinner fa-spin mr-2"></i>Calculating sales tax...
            </b-message>
        </container-list-item>

        <container-list-item no-border v-if="quote.user && !fetchingTax && taxInfo && taxInfo.success && taxInfo.taxRate > 0">
            <data-row title="Tax Jurisdiction">{{ taxInfo.jurisdiction }}</data-row>
            <data-row title="Zip Code">{{ taxInfo.address.zip }}</data-row>
            <data-row title="Washington State Tax">{{ (taxInfo.stateRate * 100).toFixed(3) }}%</data-row>
            <data-row title="Local Tax">{{ (taxInfo.localRate * 100).toFixed(3) }}%</data-row>
            <data-row title="Total Tax Rate"><strong>{{ (taxInfo.taxRate * 100).toFixed(3) }}%</strong></data-row>
            <data-row title="Quoted Price" v-if="quote.total && quote.total > 0">{{ quote.total | currency }}</data-row>
            <data-row title="Sales Tax" v-if="quote.total && quote.total > 0">{{ taxInfo.taxAmount | currency }} <small>(included in price)</small></data-row>
        </container-list-item>

        <container-list-item padded v-if="quote.user && !fetchingTax && taxInfo && taxInfo.success && taxInfo.taxRate === 0">
            <b-message type="is-info">
                <strong>No sales tax applicable</strong><br>
                <span v-if="taxInfo.reason">{{ taxInfo.reason }}</span>
            </b-message>
        </container-list-item>

        <container-list-item padded v-if="quote.user && !fetchingTax && taxInfo && !taxInfo.success">
            <b-message type="is-warning">
                <strong>Unable to calculate sales tax</strong><br>
                <span v-if="taxInfo.error">{{ taxInfo.error }}</span>
            </b-message>
        </container-list-item>

        <subheader-list-item icon="fa-chalkboard-teacher" title="Courses" :badge="coursesBadge" />

        <container-list-item padded v-if="quote.enrollmentType === 'payment_plan' && remainingHours > 0">
            <b-message type="is-danger">
                Note: Courses do not add up to the specified clock hours ({{ quote.hours }}). You need {{ remainingHours }} more hours.
            </b-message>
        </container-list-item>

        <container-list-item padded v-if="quote.enrollmentType === 'payment_plan' && remainingHours < 0">
            <b-message type="is-danger">
                Note: Courses exceed the specified clock hours ({{ quote.hours }}) by {{ Math.abs(remainingHours) }} hours.
            </b-message>
        </container-list-item>

        <card-list-item class="pb-5" v-for="c in quote.courses">
            <template #icon><i class="fa-fw fas fa-chalkboard-teacher"></i></template>
            <template #content>
                <div class="mb-3"><strong>{{ c.course.name }}</strong></div>
                <div class="columns">
                    <div class="column">
                        <b-select expanded placeholder="Clock Hours" v-model="c.config_id">
                            <option :value="cc.id" v-for="cc in c.course.configurations">{{ cc.units }} Hours</option>
                        </b-select>
                    </div>
                    <div class="column">
                        <b-select expanded placeholder="Custom 1" v-model="c.custom1">
                            <option :value="cc" v-for="cc in c.course.custom1">{{ cc }}</option>
                        </b-select>
                    </div>
                    <div class="column">
                        <b-select expanded placeholder="Custom 2" v-model="c.custom2">
                            <option :value="cc" v-for="cc in c.course.custom2">{{ cc }}</option>
                        </b-select>
                    </div>
                    <div class="column is-2 has-text-right">
                        <p class="control">
                            <button class="button is-primary" @click="removeCourse(c)"><i class="far fa-fw fa-times"></i></button>
                        </p>
                    </div>
                </div>
            </template>
        </card-list-item>

        <card-list-item class="pb-5">
            <template #icon><i class="fa-fw fas fa-plus"></i></template>
            <template #content>
                <div class="columns">
                    <div class="column is-10">
                        <b-select expanded placeholder="Choose Course" v-model="courseSelect">
                            <option :value="c" v-for="c in courses">{{ c.name }}</option>
                        </b-select>
                    </div>
                    <div class="column has-text-right">
                        <p class="control">
                            <button class="button is-primary" :disabled="!courseSelect" @click="addCourse"><i class="far fa-fw fa-plus"></i></button>
                        </p>
                    </div>
                </div>
            </template>
        </card-list-item>
    </card-list>
</template>

<script>
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import CardListItem from "@/TIER/components/CardListItem";
    import DataRow from "@/TIER/components/DataRow";

    import debounce from "debounce";

    import {client as http} from '../../http_client';

    export default {
        name: 'QuoteForm',
        props: {
            quote: {
                type: Object,
                required: true
            },
            card: {}
        },
        components: {DataRow, CardListItem, ContainerListItem, SubheaderListItem, CardList},
        data: function () {
            return {
                isFetching: false,
                userQuery: null,
                courses: [],
                courseSelect: null,
                hoursValid: false,
                taxInfo: null,
                fetchingTax: false,
                lastFetchedUserId: null
            };
        },
        computed: {
            totalCourseHours: function() {
                let total = 0;
                if (this.quote.courses && this.quote.courses.length) {
                    for (let i = 0; i < this.quote.courses.length; i++) {
                        let course = this.quote.courses[i];
                        for (let j = 0; j < course.course.configurations.length; j++) {
                            const config = course.course.configurations[j];
                            if (config.id === course.config_id) {
                                total += config.units;
                                break;
                            }
                        }
                    }
                }
                return total;
            },
            coursesBadge: function() {
                const courseCount = this.quote.courses ? this.quote.courses.length : 0;
                const hours = this.totalCourseHours;
                return `${courseCount} course${courseCount !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
            },
            remainingHours: function () {
                // For cart enrollment, no validation needed
                if (this.quote.enrollmentType === 'cart_enrollment') {
                    this.quote.valid = this.quote.courses && this.quote.courses.length > 0;
                    return 0;
                }

                // For payment plan, validate hours match
                let total = this.totalCourseHours;
                this.quote.valid = (total > 0 && parseInt(this.quote.hours) === total);
                return this.quote.hours - total;
            },
            calculatedTotal: function () {
                if (this.quote.enrollmentType === 'cart_enrollment') {
                    // For cart enrollment, calculate from total course hours at $9/hour
                    const hours = this.totalCourseHours;
                    const total = hours * 9;
                    this.quote.total = total;
                    return total;
                } else {
                    // For payment plan, use the specified hours at $10/hour
                    const hours = parseFloat(this.quote.hours) || 0;
                    const total = hours * 10;

                    // Update quote.total to keep it in sync
                    this.quote.total = total;

                    return total;
                }
            },
            monthly: function () {
                const term = parseInt(this.quote.term);
                const hours = parseFloat(this.quote.hours) || 0;

                // Don't calculate if term is not in valid range (2-48) or hours is 0
                if (!term || term < 2 || term > 48 || hours === 0) {
                    return 0;
                }

                return Math.ceil((this.calculatedTotal - this.quote.down) / term);
            },
            deferYears() {
                const currentYear = new Date().getFullYear();
                return [currentYear, currentYear + 1, currentYear + 2];
            }
        },
        methods: {
            onEnrollmentTypeChange: function() {
                if (this.quote.enrollmentType === 'cart_enrollment') {
                    // Set term to 0 for cart enrollment
                    this.quote.term = 0;
                    // Auto-calculate hours from courses
                    this.quote.hours = this.totalCourseHours;
                } else {
                    // Reset to default payment plan values
                    if (this.quote.term === 0) {
                        this.quote.term = null;
                    }
                }
            },
            searchUsers: debounce(function (name) {
                if (!name.length) {
                    this.userQuery = [];
                    return;
                }
                this.isFetching = true;
                http.get(`users/search/${name}/0/10`).then(response => {
                    this.userQuery = response.data.users;
                }).catch((error) => {
                    this.userQuery = [];
                    throw error;
                }).finally(() => {
                    this.isFetching = false;
                });
            }, 800),
            selectUser(option) {
                this.quote.user = option;
            },
            loadCourses: function () {
                this.$emit('loading', true);
                http.get(`courses/quotable`).then(response => {
                    this.courses = response.data;
                    this.$emit('loading', false);
                });
            },
            addCourse: function () {
                this.quote.courses.push({
                    course: this.courseSelect,
                    course_name: this.courseSelect.name,
                    course_id: this.courseSelect.id,
                    config_id: this.courseSelect.configurations[0].id
                });
                this.courseSelect = null;
            },
            removeCourse: function (course) {
                this.quote.courses.splice(this.quote.courses.indexOf(course), 1);
            },
            validateHours: function () {
                if (this.quote.hours < 0) this.quote.hours = 0;
                this.hoursValid = (this.quote.hours && this.quote.hours > 0 && this.quote.hours >= 10);
            },
            setDeferDay: function () {
                // Always set defer day to 1
                this.quote.defer_day = 1;
            },
            fetchTaxRate: function() {
                if (!this.quote.user || !this.quote.user.id) {
                    this.taxInfo = null;
                    this.lastFetchedUserId = null;
                    return;
                }

                this.fetchingTax = true;
                this.lastFetchedUserId = this.quote.user.id;

                // Fetch with a nominal amount just to get the rates
                // Pass address fields to use current values instead of what's in DB
                http.post('quotes/calculate-tax', {
                    userId: this.quote.user.id,
                    amount: 100,
                    address: this.quote.user.address,
                    city: this.quote.user.city,
                    state: this.quote.user.state,
                    zip: this.quote.user.zip
                }).then(response => {
                    this.taxInfo = response.data;
                    // Recalculate with actual amount if we have one
                    this.recalculateTax();
                }).catch(error => {
                    console.error('Tax calculation failed:', error);
                    this.taxInfo = null;
                    this.lastFetchedUserId = null;
                }).finally(() => {
                    this.fetchingTax = false;
                });
            },
            debouncedFetchTax: debounce(function() {
                if (this.quote.user && this.quote.user.id) {
                    this.fetchTaxRate();
                }
            }, 1000),
            recalculateTax: function() {
                // Don't do anything if we don't have tax info yet
                if (!this.taxInfo || !this.taxInfo.success) {
                    return;
                }

                // Don't do anything if we don't have a valid total
                if (!this.quote.total || this.quote.total <= 0) {
                    return;
                }

                // Only recalculate if we have tax rates (WA state)
                if (this.taxInfo.taxRate > 0) {
                    const amount = parseFloat(this.quote.total);
                    this.taxInfo.taxAmount = Math.round((amount * this.taxInfo.taxRate) * 100) / 100;
                }
            }
        },
        watch: {
            'quote.hours': function () {
                // Force recalculation of monthly payment when hours change
                this.$forceUpdate();
            },
            'quote.term': function () {
                if (parseInt(this.quote.term) === 1) {
                    let m = new Date().getMonth() + 1;
                    this.quote.defer_month = (m < 10) ? '0' + m : m.toString();
                    let y = new Date().getFullYear();
                    this.quote.defer_year = y.toString();
                }
                // Always ensure defer day is 1
                this.setDeferDay();

                // Force recalculation of monthly payment when term changes
                this.$forceUpdate();
            },
            'quote.defer_month': function () {
                this.setDeferDay();
            },
            'quote.defer_year': function () {
                this.setDeferDay();
            },
            'quote.user': function(newUser, oldUser) {
                // Only fetch tax if user changed (not just updated)
                if (newUser && (!oldUser || newUser.id !== oldUser.id)) {
                    this.fetchTaxRate();
                }
            },
            'quote.total': function() {
                this.recalculateTax();
            },
            'quote.courses': {
                deep: true,
                handler: function() {
                    // Auto-update hours for cart enrollment
                    if (this.quote.enrollmentType === 'cart_enrollment') {
                        this.quote.hours = this.totalCourseHours;
                    }
                }
            }
        },
        mounted: function () {
            this.loadCourses();
            this.validateHours();

            // Initialize enrollmentType based on term
            if (!this.quote.enrollmentType) {
                this.quote.enrollmentType = (this.quote.term === 0) ? 'cart_enrollment' : 'payment_plan';
            }

            // Always ensure defer day is set to 1
            this.setDeferDay();

            // Fetch tax rate if editing a quote with an existing user
            if (this.quote.user && this.quote.user.id) {
                this.fetchTaxRate();
            }

            // Emit mounted event to parent
            this.$emit('mounted');
        }
    };

</script>

<style scoped>
</style>
